import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchChildProfile,
  getChildProgressSummary,
} from '../../services/children';

// Async thunk for fetching child profile
export const fetchChildProfileThunk = createAsyncThunk(
  'childProfile/fetchChildProfile',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await fetchChildProfile(userId);

      if (error) {
        return rejectWithValue(error);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for fetching child progress summary
export const fetchChildProgressSummaryThunk = createAsyncThunk(
  'childProfile/fetchChildProgressSummary',
  async (childId, { rejectWithValue }) => {
    try {
      const { data, error } = await getChildProgressSummary(childId);

      if (error) {
        return rejectWithValue(error);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const childProfileSlice = createSlice({
  name: 'childProfile',
  initialState: {
    childProfile: null,
    progressSummary: null,
    loading: false,
    progressLoading: false,
    error: null,
  },
  reducers: {
    clearChildProfile: (state) => {
      state.childProfile = null;
      state.progressSummary = null;
      state.error = null;
      state.loading = false;
      state.progressLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateChildProfile: (state, action) => {
      if (state.childProfile) {
        state.childProfile = { ...state.childProfile, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch child profile
      .addCase(fetchChildProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChildProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.childProfile = action.payload;
        state.error = null;
      })
      .addCase(fetchChildProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Fetch child progress summary
      .addCase(fetchChildProgressSummaryThunk.pending, (state) => {
        state.progressLoading = true;
        state.error = null;
      })
      .addCase(fetchChildProgressSummaryThunk.fulfilled, (state, action) => {
        state.progressLoading = false;
        state.progressSummary = action.payload;
        state.error = null;
      })
      .addCase(fetchChildProgressSummaryThunk.rejected, (state, action) => {
        state.progressLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearChildProfile, clearError, updateChildProfile } =
  childProfileSlice.actions;

// Selectors
export const selectChildProfile = (state) => state.childProfile.childProfile;
export const selectChildProgressSummary = (state) =>
  state.childProfile.progressSummary;
export const selectChildProfileLoading = (state) => state.childProfile.loading;
export const selectChildProgressLoading = (state) =>
  state.childProfile.progressLoading;
export const selectChildProfileError = (state) => state.childProfile.error;
export const selectMinSchoolYear = (state) =>
  state.childProfile.childProfile?.school_year;

export default childProfileSlice.reducer;
